import { OrdersApiService } from '@/common/services/orders.api.service';
import { OrderItemsApiService } from '@/common/services/order-items.api.service';
import { OrderAddressesApiService } from '@/common/services/order-addresses.api.service';
import { OrderDiscountsApiService } from '@/common/services/order-discounts.api.service';
import { OrderShippingMethodsApiService } from '@/common/services/order-shipping-methods.api.service';
import { OrderPaymentMethodsApiService } from '@/common/services/order-payment-methods.api.service';
import { OrderCustomerAccountsApiService } from '@/common/services/order-customer-accounts.api.service';
import { OrderModel } from '@/common/models/order.model';
import { OrderItemModel } from '@/common/models/order-item.model';
import { OrderDiscountModel } from '@/common/models/order-discount.model';
import { OrderShippingMethodModel } from '@/common/models/order-shipping-method.model';
import { ProductImagesApiService } from '@/common/services/product-images.api.service';
import { ProductImageModel } from '@/common/models/product-image.model';
import { ImagesApiService } from '@/common/services/images.api.service';
import REQUEST_STATUS from '../../common/enums/requestStatus';
import ORDER_STATE from '../../common/enums/orderState';

export default {
  namespaced: true,

  state: {
    status: REQUEST_STATUS.LOADING,
    total: 0,
    lastOrderChange: null,
    items: [],
  },

  getters: {
    getAllOrders: state => state.items,
    getStatus: state => state.status,
    getTotal: state => state.total,
    getOrder: state => id => state.items.find(item => item.id === id),
    getLastChange: state => state.lastOrderChange,
  },

  mutations: {
    SET_ITEMS(state, data) {
      state.items = data;
    },
    SET_ITEM(state, data) {
      const arrayIndex = state.items.findIndex(item => item.id === data.id);

      if (arrayIndex === -1) {
        state.items.push(data);
      } else {
        this.$app.$set(state.items, arrayIndex, data);
      }
    },
    SET_TOTAL(state, data) {
      state.total = data;
    },
    SET_ORDER_ITEMS(state, payload) {
      const order = state.items.find(item => item.id === payload.orderId);
      order.extendOrder({ [OrderModel.SUB_ITEMS]: payload.items });
    },
    SET_ORDER_ADDRESSES(state, payload) {
      const order = state.items.find(item => item.id === payload.orderId);
      order.extendOrder({ [OrderModel.SUB_ADDRESSES]: payload.items });
    },
    SET_ORDER_DISCOUNTS(state, payload) {
      const order = state.items.find(item => item.id === payload.orderId);
      order.extendOrder({ [OrderModel.SUB_DISCOUNTS]: payload.items });
    },
    SET_ORDER_SHIPPING_METHODS(state, payload) {
      const order = state.items.find(item => item.id === payload.orderId);
      order.extendOrder({ [OrderModel.SUB_SHIPPING_METHODS]: payload.items });
    },
    SET_ORDER_PAYMENT_METHODS(state, payload) {
      const order = state.items.find(item => item.id === payload.orderId);
      order.extendOrder({ [OrderModel.SUB_PAYMENT_METHODS]: payload.items });
    },
    SET_ORDER_CUSTOMER_ACCOUNTS(state, payload) {
      const order = state.items.find(item => item.id === payload.orderId);
      order.extendOrder({ [OrderModel.SUB_CUSTOMER_ACCOUNTS]: payload.items });
    },
    SET_ORDER_ITEM_IMAGE(state, payload) {
      const order = state.items.find(item => item.id === payload.orderId);

      if (!order) {
        return;
      }

      const orderItem = order.items.find(item => item.id === payload.orderItemId);

      if (!orderItem) {
        return;
      }

      orderItem.extendOrderItem({ [OrderItemModel.EXT_IMAGE]: payload.image });
    },
    SET_LAST_ORDER_CHANGE(state, payload) {
      state.lastOrderChange = payload;
    },
    FETCH_REQUEST(state) {
      state.status = REQUEST_STATUS.LOADING;
    },
    FETCH_SUCCESS(state) {
      state.status = REQUEST_STATUS.SUCCESS;
    },
    FETCH_FAIL(state) {
      state.status = REQUEST_STATUS.FAIL;
    },
  },

  actions: {
    fetchAllOrders({ commit, dispatch, rootGetters }, { options, searchQuery, filterData }) {
      commit('FETCH_REQUEST');

      const moreFieldThanPriceAndDate = 2;

      return OrdersApiService.getAll(builder => {
        builder.whereIn(['payment_method_id'], rootGetters['paymentMethods/getPaymentMethodsIds']);

        if (
          filterData &&
          (Object.keys(filterData).length > moreFieldThanPriceAndDate ||
            Object.keys(filterData.price).length ||
            Object.keys(filterData.createdAt).length)
        ) {
          if (filterData.id && !Number.isNaN(parseInt(filterData.id.trim(), 10))) {
            builder.whereEquals(['id'], Number(filterData.id.trim()));
          }
          if (filterData.customerName) {
            builder.whereContains(['customer_name'], filterData.customerName.trim());
          }
          if (filterData.companyName) {
            builder.whereContains(['company_name'], filterData.companyName.trim());
          }
          if (filterData.email) {
            builder.whereContains(['email'], filterData.email.trim());
          }
          if (filterData.phone) {
            builder.whereContains(['phone'], filterData.phone.trim().split(' ').join(''));
          }
          if (filterData.createdAt) {
            if (filterData.createdAt.from) {
              builder.whereGreaterOrEqual(['created_at'], filterData.createdAt.from.trim());
            }
            if (filterData.createdAt.to) {
              builder.whereLessOrEqual(['created_at'], filterData.createdAt.to.trim());
            }
          }
          if (filterData.price) {
            if (filterData.price.from && filterData.price.from >= 0) {
              builder.whereGreaterOrEqual(['taxed_price'], filterData.price.from);
            }
            if (filterData.price.to) {
              builder.whereLessOrEqual(['taxed_price'], filterData.price.to);
            }
          }
          if (filterData.countries && filterData.countries.length > 0) {
            builder.whereIn(['country'], filterData.countries);
          }
          if (filterData.customerGroups && filterData.customerGroups.length > 0) {
            builder.whereIn(['customer_group'], filterData.customerGroups);
          }
          if (filterData.shippingMethods && filterData.shippingMethods.length > 0) {
            builder.whereIn(['shipping_method_id'], filterData.shippingMethods);
          }
          if (filterData.shippingOptions && filterData.shippingOptions.length > 0) {
            builder.whereIn(
              ['shipping_option_id'],
              filterData.shippingOptions.map(id => Number(id))
            );
          }
          if (filterData.paymentMethods && filterData.paymentMethods.length > 0) {
            builder.whereIn(['payment_method_id'], filterData.paymentMethods);
          }
          if (filterData.orderStates && filterData.orderStates.length > 0) {
            builder.whereIn(['order_state_id'], filterData.orderStates);
          }
          if (filterData.brandId) {
            builder.whereEquals(['product_brand_id'], filterData.brandId);
          }
          if (filterData.productName && filterData.productName.length > 0) {
            builder.whereContains(['product_name'], filterData.productName);
          }
          if (filterData.productSKU && filterData.productSKU.length > 0) {
            builder.whereEquals(['product_sku'], filterData.productSKU);
          }
          if (filterData.productEan) {
            builder.whereEquals(['product_ean'], filterData.productEan.trim());
          }
        } else if (searchQuery) {
          if (Number.isNaN(parseInt(searchQuery.trim(), 10))) {
            builder.whereContains(['customer_name'], searchQuery.trim());
          } else {
            builder.whereEquals(['id', 'customer_id'], Number(searchQuery.trim()));
          }
        } else {
          builder.whereEquals([OrderModel.ATTR_ORDER_STATE_ID], ORDER_STATE.PACKING);
        }

        if (options) {
          builder.getPage(options.page);
          builder.getPerPage(options.itemsPerPage);
          if (options.sortDesc && options.sortDesc[0]) {
            builder.sortBy(options.sortBy);
          } else if (options.sortBy && options.sortBy.length) {
            builder.sortByDesc(options.sortBy);
          } else {
            builder.sortByDesc([OrderModel.ATTR_CREATED_AT]);
          }
        }

        builder.embed([
          OrderModel.EMBED_STATE,
          OrderModel.EMBED_CUSTOMER_NAME,
          OrderModel.EMBED_SHIPPING,
          OrderModel.EMBED_PAYMENT,
          OrderModel.EMBED_INVOICES,
          OrderModel.EMBED_TAXED_ITEMS_PRICE,
          OrderModel.EMBED_TAX_AMOUNT,
        ]);
      })
        .then(response => {
          commit('SET_ITEMS', response.getItems());
          commit('SET_TOTAL', response.getMeta().records);
          commit('FETCH_SUCCESS');
        })
        .catch(response => {
          dispatch('handleError', response);
        });
    },

    fetchOrder({ commit, dispatch }, id) {
      commit('FETCH_REQUEST');

      return OrdersApiService.get(id, builder => {
        builder.embed([
          OrderModel.EMBED_STATE,
          OrderModel.EMBED_CUSTOMER_NAME,
          OrderModel.EMBED_SHIPPING,
          OrderModel.EMBED_PAYMENT,
          OrderModel.EMBED_INVOICES,
          OrderModel.EMBED_TAXED_ITEMS_PRICE,
          OrderModel.EMBED_TAX_AMOUNT,
        ]);
      })
        .then(response => {
          commit('SET_ITEM', response.getItem());
          commit('FETCH_SUCCESS');

          dispatch('fetchOrderItems', id);
          dispatch('fetchOrderAddresses', id);
          dispatch('fetchOrderDiscounts', id);
          dispatch('fetchOrderShippingMethods', id);
          dispatch('fetchOrderPaymentMethods', id);
          dispatch('fetchOrderCustomerAccounts', id);
        })
        .catch(response => {
          dispatch('handleError', response);
          return Promise.reject(response);
        });
    },

    updateOrder({ commit, dispatch }, { id, formData }) {
      commit('FETCH_REQUEST');

      return OrdersApiService.patch(id, formData)
        .then(response => {
          commit('FETCH_SUCCESS');
          commit('SET_ITEM', response.getItem());
          return response.getItem();
        })
        .catch(response => {
          dispatch('handleError', response);
          return Promise.reject();
        });
    },

    fetchOrderItems({ commit, dispatch }, id) {
      commit('FETCH_REQUEST');

      return OrderItemsApiService.getAll(id, builder => {
        builder.sortBy(OrderItemModel.ATTR_SKU);
        builder.embed([OrderItemModel.EMBED_VARIATION_PROPERTIES_NAMES]);
      })
        .then(response => {
          const items = response.getItems();
          dispatch('fetchOrderItemsImages', { orderItems: items, orderId: id });
          commit('SET_ORDER_ITEMS', { orderId: id, items });
          commit('FETCH_SUCCESS');
        })
        .catch(response => {
          dispatch('handleError', response);
        });
    },

    fetchOrderAddresses({ commit, dispatch }, id) {
      commit('FETCH_REQUEST');

      return OrderAddressesApiService.getAll(id)
        .then(response => {
          commit('SET_ORDER_ADDRESSES', { orderId: id, items: response.getItems() });
          commit('FETCH_SUCCESS');
        })
        .catch(response => {
          dispatch('handleError', response);
        });
    },

    fetchOrderDiscounts({ commit, dispatch }, id) {
      commit('FETCH_REQUEST');

      return OrderDiscountsApiService.getAll(id, builder => {
        builder.sortBy([OrderDiscountModel.ATTR_PRIORITY]);
      })
        .then(response => {
          commit('SET_ORDER_DISCOUNTS', { orderId: id, items: response.getItems() });
          commit('FETCH_SUCCESS');
        })
        .catch(response => {
          dispatch('handleError', response);
        });
    },

    fetchOrderShippingMethods({ commit, dispatch }, id) {
      commit('FETCH_REQUEST');

      return OrderShippingMethodsApiService.getAll(id, builder => {
        builder.embed([OrderShippingMethodModel.EMBED_SHIPPING_OPTION, OrderShippingMethodModel.EMBED_SHIPPING_METHOD]);
      })
        .then(response => {
          commit('SET_ORDER_SHIPPING_METHODS', { orderId: id, items: response.getItems() });
          commit('FETCH_SUCCESS');
        })
        .catch(response => {
          dispatch('handleError', response);
        });
    },

    fetchOrderPaymentMethods({ commit, dispatch }, id) {
      commit('FETCH_REQUEST');

      return OrderPaymentMethodsApiService.getAll(id)
        .then(response => {
          commit('SET_ORDER_PAYMENT_METHODS', { orderId: id, items: response.getItems() });
          commit('FETCH_SUCCESS');
        })
        .catch(response => {
          dispatch('handleError', response);
        });
    },

    fetchOrderCustomerAccounts({ commit, dispatch }, id) {
      commit('FETCH_REQUEST');

      return OrderCustomerAccountsApiService.getAll(id)
        .then(response => {
          commit('SET_ORDER_CUSTOMER_ACCOUNTS', { orderId: id, items: response.getItems() });
          commit('FETCH_SUCCESS');
        })
        .catch(response => {
          dispatch('handleError', response);
        });
    },

    fetchOrderItemsImages({ commit, dispatch }, { orderItems, orderId }) {
      commit('FETCH_REQUEST');

      const productIds = orderItems.filter(orderItem => orderItem.productId).map(orderItem => orderItem.productId);

      if (!productIds.length) {
        return;
      }

      return ProductImagesApiService.getAll(builder => {
        builder.whereIn([ProductImageModel.ATTR_PRODUCT_ID], productIds);
      })
        .then(response => {
          const productImages = response.getItems();

          productImages.forEach(productImage => {
            ImagesApiService.get(productImage.imageId)
              .then(response => {
                const image = response.getItem();
                const orderItem = orderItems.find(orderItem => orderItem.productId === productImage.productId);

                if (!orderItem) {
                  return;
                }

                commit('SET_ORDER_ITEM_IMAGE', { orderId, orderItemId: orderItem.id, image });
              })
              .catch(() => {});
          });
          commit('FETCH_SUCCESS');
        })
        .catch(response => {
          dispatch('handleError', response);
        });
    },

    setLastChange({ commit }, payload) {
      commit('SET_LAST_ORDER_CHANGE', payload);
    },

    handleError({ commit }, response) {
      commit('FETCH_FAIL');
      const message = response.getFirstValidationErrorMessage() || response.getMessage();
      this.$app.$alertError(message);
    },
  },
};
